/**
 * 统一 API 代理（mock/真实自动分发）
 * 原 mockApi.ts 代码已迁移
 */

import { categoryMockData } from '@/mock/category';
import { brandMockData } from '@/mock/brand';
import * as orderMockData from '@/mock/order';
import { request, get, post, put, del, patch } from './request';
import { mockHandler } from '../mock/mockHandler';

// 读取全局配置
import config from '../../config.json';

const delay = (ms: number = 300) => new Promise(resolve => setTimeout(resolve, ms));

interface ApiResponse<T = any> {
  success: boolean;
  code: number;
  data: T;
  message: string;
}

function shouldUseMock(configParam?: any): boolean {
  // 优先使用参数配置，其次全局配置
  if (typeof configParam?.useMock !== 'undefined') {
    return configParam.useMock;
  }
  return config.useMock === true;
}

async function handleApiCall<T>(mockHandler: () => Promise<ApiResponse<T>>, realHandler: () => Promise<ApiResponse<T>>, config?: any): Promise<ApiResponse<T>> {
  if (shouldUseMock(config)) {
    return await mockHandler();
  } else {
    try {
      const response = await realHandler();
      return response;
    } catch (error: any) {
      return {
        success: false,
        code: error?.response?.status || 500,
        data: null as unknown as T,
        message: error?.message || 'API 调用失败'
      };
    }
  }
}

/**
 * 确保 URL 包含 /api/v1/ 前缀（开发环境 vite 代理需要此前缀路由到后端服务）
 */
function ensureApiPrefix(url: string): string {
  if (url.startsWith('/api/')) return url;
  return `/api/v1${url.startsWith('/') ? '' : '/'}${url}`;
}

async function realHandler<T>(method: 'get' | 'post' | 'put' | 'delete' | 'patch', url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
  try {
    url = ensureApiPrefix(url);
    let response;
    if (method === 'get') {
      response = await get<T>(url, config?.params, config);
    } else if (method === 'post') {
      response = await post<T>(url, data, config);
    } else if (method === 'put') {
      response = await put<T>(url, data, config);
    } else if (method === 'delete') {
      response = await del<T>(url, config?.params, config);
    } else if (method === 'patch') {
      response = await patch<T>(url, data, config);
    }

    return {
      success: true,
      code: 200,
      data: response as T,
      message: '操作成功'
    };
  } catch (error: any) {
    console.error('API调用失败:', error);
    const respData = error?.response?.data as any;
    const details: any[] = respData?.details || respData?.detail || [];
    const detailMsg = details[0]?.message;
    const bodyMsg = respData?.error || respData?.msg || respData?.message;
    const status = error?.response?.status || 500;
    // 透传业务错误响应，保留 details 供表单展示具体校验原因。
    const errorData = (respData as unknown as T) ?? (null as unknown as T);
    return {
      success: false,
      code: status,
      data: errorData,
      message: detailMsg || bodyMsg || error?.message || 'API 调用失败'
    };
  }
}

// 支持 PATCH 方法
// mockHandler 已迁移到 mockHandler.ts

export const unifiedApi = {
  async get<T>(url: string, config?: { params?: any; useMock?: boolean }): Promise<ApiResponse<T>> {
    return handleApiCall<T>(
      () => mockHandler<T>('get', url, undefined, config),
      () => realHandler<T>('get', url, undefined, config),
      config
    );
  },
  async post<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    return handleApiCall<T>(
      () => mockHandler<T>('post', url, data, config),
      () => realHandler<T>('post', url, data, config),
      config
    );
  },
  async put<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    return handleApiCall<T>(
      () => mockHandler<T>('put', url, data, config),
      () => realHandler<T>('put', url, data, config),
      config
    );
  },
  async delete<T>(url: string, config?: any): Promise<ApiResponse<T>> {
    return handleApiCall<T>(
      () => mockHandler<T>('delete', url, undefined, config),
      () => realHandler<T>('delete', url, undefined, config),
      config
    );
  },
  async patch<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    return handleApiCall<T>(
      () => mockHandler<T>('patch', url, data, config),
      () => realHandler<T>('patch', url, data, config),
      config
    );
  },

  /**
   * 微服务API请求 (新增方法)
   * 根据模块名称自动路由到对应的微服务
   *
   * @example
   * // 调用业务服务的订单API
   * unifiedApi.service('order').get<OrderList>('/list')
   *
   * // 调用平台服务的用户API
   * unifiedApi.service('user').post<User>('/create', userData)
   */
  service(module: any) {
    // 动态导入，避免循环依赖
    const buildApiPath = (moduleName: string, path: string) => {
      // 在运行时动态导入
      return import('@/api/gateway').then(gateway => gateway.buildApiPath(moduleName as any, path));
    };

    return {
      get: async <T = any>(path: string, config?: any): Promise<ApiResponse<T>> => {
        const apiPath = await buildApiPath(module, path);
        return unifiedApi.get<T>(apiPath, config);
      },

      post: async <T = any>(path: string, data?: any, config?: any): Promise<ApiResponse<T>> => {
        const apiPath = await buildApiPath(module, path);
        return unifiedApi.post<T>(apiPath, data, config);
      },

      put: async <T = any>(path: string, data?: any, config?: any): Promise<ApiResponse<T>> => {
        const apiPath = await buildApiPath(module, path);
        return unifiedApi.put<T>(apiPath, data, config);
      },

      delete: async <T = any>(path: string, config?: any): Promise<ApiResponse<T>> => {
        const apiPath = await buildApiPath(module, path);
        return unifiedApi.delete<T>(apiPath, config);
      },

      patch: async <T = any>(path: string, data?: any, config?: any): Promise<ApiResponse<T>> => {
        const apiPath = await buildApiPath(module, path);
        return unifiedApi.patch<T>(apiPath, data, config);
      },
    };
  }
};

export default unifiedApi;
